/**
 * React Query retry policy.
 *
 * The default `retry: 3` turned a rate limit into a self-inflicted outage: the
 * Tervek page fired ~24 requests, the shared per-handler throttler bucket
 * returned 429, and every one of those was retried three more times — up to ~96
 * requests for a single page load, all aimed at the bucket that was already
 * full. Retrying a rate limit is the one case where retrying is guaranteed to
 * make things worse.
 *
 * Errors are recognised by their marker flags rather than by importing the error
 * classes, which keeps this module dependency-free (and matches the convention
 * `SessionExpiredError` already established with its public `isSessionExpired`
 * flag for exactly this kind of downstream branching).
 */

/** Retries after the initial attempt for errors that are worth retrying. */
export const MAX_QUERY_RETRIES = 2;

/** Never wait longer than this between retries. */
const MAX_RETRY_DELAY_MS = 30_000;

function hasFlag(error: unknown, flag: string): boolean {
  return (
    typeof error === 'object' && error !== null && (error as Record<string, unknown>)[flag] === true
  );
}

/** True for a 429 thrown by `authFetch`. */
export function isRateLimitedError(error: unknown): boolean {
  return hasFlag(error, 'isRateLimited');
}

/** True once a 401 has survived the refresh attempt. */
export function isSessionExpiredError(error: unknown): boolean {
  return hasFlag(error, 'isSessionExpired');
}

/**
 * Should React Query retry this failed query?
 *
 * - **429 → never.** This is the whole point: another attempt lands in the same
 *   full bucket and pushes the reset further out.
 * - **Session expired → never.** The refresh already failed; retrying cannot
 *   produce a valid token, it just delays the redirect to /login.
 * - Anything else (5xx, network blip) → retried up to `MAX_QUERY_RETRIES` with
 *   the exponential backoff below.
 *
 * Note this governs AUTOMATIC retries only. An explicit `refetchQueries` from a
 * user-clicked "Újrapróbálás" button is not a retry and is unaffected.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isRateLimitedError(error)) return false;
  if (isSessionExpiredError(error)) return false;
  return failureCount < MAX_QUERY_RETRIES;
}

/**
 * Exponential backoff: 1s, 2s, 4s … capped. `attemptIndex` is 0-based, matching
 * React Query's `retryDelay` contract.
 *
 * 429 never reaches this (it is not retried), so there is no Retry-After to
 * honour here — the header is captured on the error itself for the UI to show.
 */
export function queryRetryDelayMs(attemptIndex: number): number {
  return Math.min(MAX_RETRY_DELAY_MS, 1_000 * 2 ** attemptIndex);
}
